import { ComponentMultiStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';

import { SelectProps } from './Select';

import { convertHexToRGBAString } from '_helpers';
import { FONT_WEIGHTS } from 'theme/Typography';
import COLORS from 'theme/colors';
import { getElevatedShadow, getFocusedOutlineShadow } from 'theme/shadows';

const SelectTheme: ComponentMultiStyleConfig = {
  parts: ['field', 'icon'],
  baseStyle: () => ({
    field: {
      fontWeight: FONT_WEIGHTS.medium,
      cursor: 'pointer',
      _light: {
        color: 'black.lighter',

        _hover: {
          backgroundColor: 'silver.lighter'
        },
        _active: {
          backgroundColor: 'silver.light'
        },
        _disabled: {
          cursor: 'not-allowed',
          color: 'grey.light'
        }
      },
      _dark: {
        color: 'silver.light',

        _hover: {
          backgroundColor: 'black.lightest'
        },
        _active: {
          backgroundColor: 'grey.base'
        },
        _disabled: {
          cursor: 'not-allowed',
          color: 'grey.lighter'
        }
      },

      _first: {
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    },
    icon: {
      _light: {
        color: 'black.lighter'
      },
      _dark: {
        color: 'silver.light'
      }
    }
  }),
  variants: {
    basic: (props: SelectProps & StyleFunctionProps) => ({
      field: {
        boxShadow: getElevatedShadow(props),

        _focus: {
          boxShadow: getFocusedOutlineShadow(props)
        },
        _light: {
          backgroundColor: 'silver.lightest',
          _disabled: {
            backgroundColor: 'silver.light'
          }
        },
        _dark: {
          backgroundColor: 'black.lighter',
          _disabled: {
            backgroundColor: 'black.light'
          }
        }
      }
    }),
    outline: (props: SelectProps & StyleFunctionProps) => ({
      field: {
        boxShadow: 'none',
        backgroundColor: 'transparent',

        _focus: {
          border: '1px solid transparent',
          boxShadow: getFocusedOutlineShadow(props)
        },

        _light: {
          border: `1px solid ${convertHexToRGBAString(COLORS.black.base, 0.16)}`
        },
        _dark: {
          border: `1px solid ${convertHexToRGBAString(COLORS.silver.lightest, 0.48)}`
        }
      }
    }),
    ghost: (props: SelectProps & StyleFunctionProps) => ({
      field: {
        _focus: {
          boxShadow: getFocusedOutlineShadow(props)
        },
        backgroundColor: 'transparent'
      }
    })
  },
  sizes: {
    xs: {
      field: {
        h: '24px',
        borderRadius: '6px',
        fontSize: '12px',
        ps: '8px'
      },
      icon: {
        fontSize: '12px'
      }
    },
    sm: {
      field: {
        h: '32px',
        borderRadius: '8px',
        fontSize: '14px',
        ps: '12px'
      },
      icon: {
        fontSize: '14px'
      }
    },
    md: {
      field: {
        h: '40px',
        borderRadius: '10px',
        fontSize: '17px',
        ps: '16px'
      },
      icon: {
        fontSize: '17px'
      }
    },
    lg: {
      field: {
        h: '48px',
        borderRadius: '12px',
        fontSize: '20px',
        ps: '24px'
      },
      icon: {
        fontSize: '20px'
      }
    }
  },
  defaultProps: {
    focusBorderColor: (null as unknown) as string
  }
};

export default SelectTheme;
